import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import type { Send } from '../../types/send';
import type { GradingSystemWithGrades } from '../../types/gym';
import type { TimePeriod } from '../../utils/stats-utils';
import { groupByPeriod, sortedPeriodKeys, periodLabel, gradeName, CHART_TOOLTIP_STYLE } from '../../utils/stats-utils';

interface MaxGradeOverTimeProps {
  sends: Send[];
  gradingSystem: GradingSystemWithGrades | null | undefined;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
}

export default function MaxGradeOverTime({ sends, gradingSystem, period, onPeriodChange }: MaxGradeOverTimeProps) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    const groups = groupByPeriod(sends, period);
    const keys = sortedPeriodKeys(groups);
    return keys.map(key => {
      const maxVal = groups[key].reduce<number | null>((m, s) => {
        const v = s.attributes.climb.difficulty_value;
        if (v == null) return m;
        return m == null ? v : Math.max(m, v);
      }, null);
      return {
        key,
        label: periodLabel(key, period),
        value: maxVal,
      };
    });
  }, [sends, period]);

  const grades = gradingSystem ? [...gradingSystem.grades].sort((a, b) => a.value - b.value) : [];
  const yDomain = grades.length
    ? [grades[0].value, grades[grades.length - 1].value] as [number, number]
    : undefined;

  const tickFormatter = (val: number) =>
    gradingSystem ? gradeName(val, gradingSystem.grades) : String(val);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">{t('stats.maxGradeOverTime.title')}</CardTitle>
        <ToggleGroup
          type="single"
          value={period}
          onValueChange={(v) => { if (v) onPeriodChange(v as TimePeriod); }}
          className="h-7"
        >
          <ToggleGroupItem value="week" className="h-7 px-2 text-xs">{t('stats.period.week')}</ToggleGroupItem>
          <ToggleGroupItem value="month" className="h-7 px-2 text-xs">{t('stats.period.month')}</ToggleGroupItem>
          <ToggleGroupItem value="year" className="h-7 px-2 text-xs">{t('stats.period.year')}</ToggleGroupItem>
        </ToggleGroup>
      </CardHeader>
      {data.length === 0 ? (
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('stats.insufficientData')}
        </CardContent>
      ) : (
        <CardContent className="overflow-x-auto px-2 pb-4">
          <div style={{ minWidth: 320 }}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  domain={yDomain}
                  tickFormatter={tickFormatter}
                  tick={{ fontSize: 11 }}
                  width={40}
                />
                <Tooltip
                  {...CHART_TOOLTIP_STYLE}
                  formatter={(val) => {
                    const n = typeof val === 'number' ? val : Number(val);
                    return [gradingSystem ? gradeName(n, gradingSystem.grades) : n, t('stats.maxGradeOverTime.maxGrade')];
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
